import React, { useEffect } from "react";
import { MdEmail, MdMessage, MdPhone } from "react-icons/md";
import { FiX } from "react-icons/fi";
import { FaUserTie } from "react-icons/fa";

import {
  cn,
  getMemberFullName,
  sanitizeBio,
  toAbsoluteTeamPhoto,
} from "./teamUtils";

const FALLBACK_AVATAR = "/img/user_avatar.jpg";

function ActionButton({ icon: Icon, label, onClick }) {
  return (
    <button
      type="button"
      onClick={onClick}
      className="inline-flex h-11 w-11 items-center justify-center rounded-full border border-slate-200 bg-white text-slate-700 shadow-sm transition hover:-translate-y-0.5 hover:border-primary/30 hover:bg-primary/10 hover:text-primary dark:border-white/10 dark:bg-white/5 dark:text-slate-200 dark:hover:bg-primary/10"
      title={label}
      aria-label={label}
    >
      <Icon className="text-xl" />
    </button>
  );
}

export default function TeamProfileModal({ member, onClose }) {
  const photo = toAbsoluteTeamPhoto(member?.photoURL) || FALLBACK_AVATAR;
  const fullName = getMemberFullName(member);
  const safeBio = sanitizeBio(member?.bio_member);

  useEffect(() => {
    const onKeyDown = (event) => {
      if (event.key === "Escape") onClose();
    };

    window.addEventListener("keydown", onKeyDown);
    return () => window.removeEventListener("keydown", onKeyDown);
  }, [onClose]);

  const call = () => {
    if (member?.tel_member) {
      window.open(`tel:${member.tel_member}`, "_blank");
    }
  };

  const sms = () => {
    if (member?.tel_member) {
      window.open(`sms:${member.tel_member}`, "_blank");
    }
  };

  const email = () => {
    if (member?.mail_member) {
      window.open(`mailto:${member.mail_member}`, "_blank");
    }
  };

  if (!member) return null;

  return (
    <div
      className="fixed inset-0 z-[80] flex items-center justify-center bg-slate-950/75 px-3 py-6 backdrop-blur-sm"
      onClick={onClose}
      role="dialog"
      aria-modal="true"
    >
      <div
        className="relative max-h-[92vh] w-full max-w-4xl overflow-hidden rounded-[2rem] border border-white/10 bg-white shadow-2xl dark:bg-slate-950"
        onClick={(event) => event.stopPropagation()}
      >
        <button
          type="button"
          onClick={onClose}
          className="absolute right-4 top-4 z-20 flex h-11 w-11 items-center justify-center rounded-full border border-slate-200 bg-white/85 text-slate-700 shadow-lg backdrop-blur transition hover:text-primary dark:border-white/10 dark:bg-slate-900/80 dark:text-slate-200"
          aria-label="Fermer"
        >
          <FiX className="text-xl" />
        </button>

        <div className="max-h-[92vh] overflow-y-auto overflow-x-hidden">
          <div className="grid lg:grid-cols-[0.78fr_1.22fr]">
            <div className="relative min-h-[320px] overflow-hidden lg:min-h-full">
              <img
                src={photo}
                alt={fullName || "Membre du club"}
                className="h-full w-full object-cover"
                loading="lazy"
                onError={(event) => {
                  event.currentTarget.src = FALLBACK_AVATAR;
                }}
              />

              <div className="absolute inset-0 bg-gradient-to-t from-slate-950/85 via-slate-950/20 to-transparent" />

              <div className="absolute bottom-6 left-6 right-6">
                <p className="inline-flex rounded-full border border-white/15 bg-white/15 px-4 py-2 text-xs font-bold uppercase tracking-[0.18em] text-white/75 backdrop-blur-xl">
                  {member?.categorie || "Membre"}
                </p>

                <h3 className="mt-4 font-barlow text-4xl font-extrabold uppercase leading-none text-white drop-shadow-xl">
                  {member?.nom}
                </h3>

                <p className="font-barlow text-3xl font-bold leading-tight text-white/90">
                  {member?.prenom}
                </p>
              </div>
            </div>

            <div className="relative overflow-x-hidden p-6 text-slate-900 dark:text-white sm:p-8 lg:p-10">
              <div className="relative pr-12">
                {member?.fonction && (
                  <div className="inline-flex items-center gap-2 rounded-full border border-orange-200 bg-orange-50 px-4 py-2 text-xs font-bold uppercase tracking-[0.18em] text-orange-700 dark:border-orange-500/20 dark:bg-orange-500/10 dark:text-orange-300">
                    <FaUserTie />
                    {member.fonction}
                  </div>
                )}

                <h4 className="mt-6 font-barlow text-3xl font-extrabold text-slate-950 dark:text-white">
                  Profil du membre
                </h4>

                <div className="mt-6 flex flex-wrap gap-3">
                  {member?.tel_member && (
                    <>
                      <ActionButton
                        icon={MdPhone}
                        label="Appeler"
                        onClick={call}
                      />
                      <ActionButton
                        icon={MdMessage}
                        label="Envoyer un SMS"
                        onClick={sms}
                      />
                    </>
                  )}

                  {member?.mail_member && (
                    <ActionButton
                      icon={MdEmail}
                      label="Envoyer un email"
                      onClick={email}
                    />
                  )}
                </div>

                <div className="mt-7 space-y-4">
                  {member?.mail_member && (
                    <div className="rounded-3xl border border-slate-200 bg-slate-50 p-4 dark:border-white/10 dark:bg-white/5">
                      <p className="text-xs font-bold uppercase tracking-[0.16em] text-slate-400">
                        Email
                      </p>

                      <a
                        href={`mailto:${member.mail_member}`}
                        className="mt-1 block break-all text-sm font-bold text-primary hover:underline"
                      >
                        {member.mail_member}
                      </a>
                    </div>
                  )}

                  {member?.tel_member && (
                    <div className="rounded-3xl border border-slate-200 bg-slate-50 p-4 dark:border-white/10 dark:bg-white/5">
                      <p className="text-xs font-bold uppercase tracking-[0.16em] text-slate-400">
                        Téléphone
                      </p>

                      <p className="mt-1 text-sm font-bold text-slate-700 dark:text-slate-200">
                        {member.tel_member}
                      </p>
                    </div>
                  )}

                  {member?.bio_member && (
                    <div className="rounded-3xl border border-slate-200 bg-slate-50 p-5 dark:border-white/10 dark:bg-white/5">
                      <p className="mb-3 text-xs font-bold uppercase tracking-[0.16em] text-slate-400">
                        Bio
                      </p>

                      <div
                        className={cn(
                          "prose prose-slate max-w-none dark:prose-invert",
                          "text-sm leading-7 text-slate-600 dark:text-slate-300",
                          "dark:[&_strong]:!text-white dark:[&_b]:!text-white",
                          "dark:[&_strong]:font-extrabold dark:[&_b]:font-extrabold"
                        )}
                        dangerouslySetInnerHTML={{
                          __html: safeBio,
                        }}
                      />
                    </div>
                  )}
                </div>

                <div className="mt-8">
                  <button
                    type="button"
                    onClick={onClose}
                    className="inline-flex items-center justify-center rounded-full bg-slate-950 px-6 py-3 text-sm font-bold text-white shadow-lg transition hover:-translate-y-0.5 hover:bg-slate-800 dark:bg-white dark:text-slate-950 dark:hover:bg-slate-100"
                  >
                    Fermer
                  </button>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}